import * as React from 'react'
import { Star } from 'lucide-react'
import { useWriteReview } from '@/hooks/useVendorReviews'
import { requestErrorMessage } from '@/helpers/apiClient'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Textarea } from '@/components/ui/textarea'
import { FormError } from '@/components/ui/form-error'
import { writeVendorReviewInputSchema } from '@lib/input/vendor-review.input'
import { REVIEW_BODY_MAX } from '@lib/constants/vendor-reviews'
import type { OwnEventReviewDto } from '@lib/dto/vendor-review.dto'

const STARS = [1, 2, 3, 4, 5] as const

/** Which way an arrow key moves the chosen star. */
const STEP: Readonly<Record<string, number>> = { ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1 }

interface StarPickerProps {
  /** 0 until a star is chosen. */
  value: number
  onChange: (rating: number) => void
  invalid: boolean
}

/**
 * One to five stars, as a radio group: arrow keys move the choice, as in any
 * set of radio buttons, and each star says how many it is.
 */
function StarPicker({ value, onChange, invalid }: StarPickerProps) {
  const refs = React.useRef<Array<HTMLButtonElement | null>>([])

  function move(e: React.KeyboardEvent, from: number): void {
    const step = STEP[e.key]
    if (step === undefined) return
    e.preventDefault()
    const next = Math.min(5, Math.max(1, from + step))
    onChange(next)
    refs.current[next - 1]?.focus()
  }

  return (
    <div role="radiogroup" aria-label="Stars" aria-invalid={invalid} className="flex gap-1">
      {STARS.map((n) => (
        <button
          key={n}
          ref={(el) => {
            refs.current[n - 1] = el
          }}
          type="button"
          role="radio"
          aria-checked={value === n}
          aria-label={n === 1 ? '1 star' : `${String(n)} stars`}
          // One stop in the tab order: the chosen star, or the first before any is.
          tabIndex={value === n || (value === 0 && n === 1) ? 0 : -1}
          onClick={() => onChange(n)}
          // With none chosen, the first arrow moves on from the focused star, as native radios do.
          onKeyDown={(e) => move(e, value === 0 ? n : value)}
          className="rounded-sm p-0.5 text-primary focus-visible:focus-ds"
        >
          <Star aria-hidden className={cn('h-7 w-7', n <= value ? 'fill-current' : 'fill-none text-ink-muted')} />
        </button>
      ))}
    </div>
  )
}

interface Props {
  eventId: string
  vendorId: string
  companyName: string
  /** "Jamie R. · Couple · October 2026": how it will show, said before it is sent. */
  shownAs: string
  /** The review being changed, as it was when the dialog opened; null for a first review. */
  existing: OwnEventReviewDto | null
  /** The same review as the event's list has it now, refreshed after a refused send. */
  current: OwnEventReviewDto | null
  onClose: () => void
}

/** Why the review can't be sent from this dialog any more, if it can't: it changed, or was published, since it opened. */
function staleness(existing: OwnEventReviewDto | null, current: OwnEventReviewDto | null): string | null {
  if (current !== null && (current.status === 'approved' || current.status === 'removed')) {
    return current.status === 'approved' ? 'It was published meanwhile, so it can’t be changed.' : 'It was taken down meanwhile.'
  }
  if ((existing?.sentAt ?? null) === (current?.sentAt ?? null)) return null
  return 'It changed since you opened this, in another window or from someone at your company. Close this and read it before sending yours.'
}

/**
 * Writing a review of a company that worked the event, or changing one not yet
 * published (docs/vendor-growth.spec.md, "Reviews"). The writer is told how it
 * will show, that FLOH Venues checks it first, and that it can't be changed
 * once published. It sends the review as it was when it opened, never a later
 * one: once the event's list shows it changed, the dialog says so and stops.
 */
export function ReviewDialog({ eventId, vendorId, companyName, shownAs, existing, current, onClose }: Props) {
  const write = useWriteReview(eventId)
  const stale = staleness(existing, current)
  const [rating, setRating] = React.useState(existing?.rating ?? 0)
  const [body, setBody] = React.useState(existing?.body ?? '')
  const [errors, setErrors] = React.useState<{ rating?: string | undefined; body?: string | undefined }>({})

  function submit(e: React.FormEvent): void {
    e.preventDefault()
    if (stale !== null) return
    const parsed = writeVendorReviewInputSchema.safeParse({ rating, body })
    if (!parsed.success) {
      const issue = (field: string) => parsed.error.issues.find((i) => i.path[0] === field)?.message
      setErrors({ rating: issue('rating'), body: issue('body') })
      return
    }
    setErrors({})
    // A change names the version it changes, so one sent again meanwhile isn't overwritten.
    const input = existing === null ? parsed.data : { ...parsed.data, sentAt: existing.sentAt }
    write.mutate({ vendorId, input }, { onSuccess: onClose })
  }

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Review {companyName}</DialogTitle>
          <DialogDescription>Tell others what it was like to work with them.</DialogDescription>
        </DialogHeader>
        <form className="space-y-5" onSubmit={submit} noValidate>
          <Field label="Your rating" error={errors.rating}>
            <StarPicker value={rating} onChange={setRating} invalid={errors.rating !== undefined} />
          </Field>
          <Field label="What was it like to work with them?" htmlFor="review-body" error={errors.body} hint={`${String(body.length)}/${String(REVIEW_BODY_MAX)}`}>
            <Textarea id="review-body" rows={6} maxLength={REVIEW_BODY_MAX} value={body} onChange={(e) => setBody(e.target.value)} />
          </Field>
          <p className="m-0 text-xs text-ink-muted">
            Shows as &ldquo;{shownAs}&rdquo;. FLOH Venues checks it before it&rsquo;s published, and once it is, it can&rsquo;t be changed.
          </p>
          {stale === null ? null : <FormError>{stale}</FormError>}
          {write.isError && stale === null ? <FormError>{requestErrorMessage(write.error)}</FormError> : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={write.isPending || stale !== null}>
              {write.isPending ? 'Sending…' : 'Send review'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
